<template>
	<div
		ref="wheel"
		class="wheel-select"
		:class="{ 'is-disabled': disabled }"
		@mousedown="handleMouseDown"
		@touchstart.prevent="handleTouchStart"
		@wheel.prevent="handleWheel"
	>
		<div
			ref="rulersContainer"
			class="wheel-select__ruler-container"
		>
			<div
				ref="ruler"
				class="wheel-select__ruler"
				:style="{ transform: `translateX(${offset}px)` }"
			>
				<div
					v-for="tick in ticks"
					:key="tick.value"
					:class="['wheel-select__tick', {
						'wheel-select__tick--major': tick.isMajor,
						'wheel-select__tick--active': tick.value === currentInternalValue
					}]"
					:style="{ left: tick.position + 'px' }"
				>
					<span
						v-if="tick.isMajor"
						class="wheel-select__label"
					>{{ tick.value }}</span>
				</div>

				<!-- Border that moves with the ruler -->
				<div
					class="wheel-select__border"
					:style="{ width: totalWidth + 'px' }"
				/>
			</div>

			<div
				class="wheel-select__fade-left"
				:style="fadeLeftStyle"
			/>
			<div
				class="wheel-select__fade-right"
				:style="fadeRightStyle"
			/>
		</div>
		<div class="wheel-select__center-indicator" />
	</div>
</template>

<script>
export default {
	name:  'WheelSelect',
	props: {
		min: {
			type:    Number,
			default: 0,
		},
		max: {
			type:    Number,
			default: 100,
		},
		step: {
			type:    Number,
			default: 1,
		},
		value: {
			type:    Number,
			default: null,
		},
		visibleRangePercent: {
			type:    Number,
			default: 20, // 20% of the range is visible
		},
		fadeWidth: {
			type:    Number,
			default: 30, // Width of fade as percentage of container width
		},
		defaultWidth: {
			type:    Number,
			default: 300, // Fallback width when container is hidden
		},
		tickSpacing: {
			type:    Number,
			default: 5, // Fixed 5px spacing between ticks
		},
		disabled: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			dragging:              false,
			startX:                0,
			startOffset:           0,
			containerWidth:        0,
			offset:                0,
			currentInternalValue:  0, // Initialize with a valid number
			parentBackgroundColor: null,
		};
	},
	computed: {
		range() {
			return this.max - this.min;
		},
		// Fixed tick width to 5px
		tickWidth() {
			return this.tickSpacing;
		},
		tickCount() {
			return Math.floor((this.range / this.step) + 1);
		},
		totalWidth() {
			return this.tickCount * this.tickWidth;
		},
		ticks() {
			const ticks = [];
			const majorStep = this.step * 10;
			for (let v = this.min; v <= this.max; v += this.step) {
				const isMajor = (v - this.min) % majorStep === 0;
				const position = this.valueToPosition(v);
				ticks.push({ value: v, isMajor, position });
			}
			return ticks;
		},
		defaultValue() {
			// Ensure we return a valid number
			return parseInt(Math.round((this.max + this.min) / 2), 10);
		},
		fadeLeftStyle() {
			return {
				backgroundImage: this.parentBackgroundColor
					? `linear-gradient(to right, ${this.parentBackgroundColor} 0%, rgba(0, 0, 0, 0) 100%)`
					: null,
				width: `${this.fadeWidth}%`,
			};
		},
		fadeRightStyle() {
			return {
				backgroundImage: this.parentBackgroundColor
					? `linear-gradient(to left, ${this.parentBackgroundColor} 0%, rgba(0, 0, 0, 0) 100%)`
					: null,
				width: `${this.fadeWidth}%`,
			};
		},
	},
	watch: {
		value: {
			immediate: true,
			handler(newValue) {
				const parsedValue = parseInt(newValue, 10);
				if (!isNaN(parsedValue) && parsedValue !== this.currentInternalValue) {
					this.currentInternalValue = parsedValue;
					if (this.$refs.rulersContainer) {
						this.updateOffset();
					}
				} else if (isNaN(parsedValue) || newValue === null) {
					this.currentInternalValue = this.defaultValue;
					if (this.$refs.rulersContainer) {
						this.updateOffset();
					}
				}
			},
		},
		// Watch for min/max/step changes to recalculate everything
		min() {
			this.updateWheelLayout();
		},
		max() {
			this.updateWheelLayout();
		},
		step() {
			this.updateWheelLayout();
		},
	},
	mounted() {
		this.$nextTick(() => {
			// Detect parent background color
			this.detectParentBackgroundColor();

			// Set up ResizeObserver to detect size changes
			if (window.ResizeObserver) {
				this.resizeObserver = new ResizeObserver(this.handleResize);
				this.resizeObserver.observe(this.$refs.rulersContainer);
			} else {
				// Fallback for browsers without ResizeObserver
				window.addEventListener('resize', this.handleResize);
			}

			// Set up MutationObserver to detect display changes
			if (window.MutationObserver) {
				this.setupVisibilityObserver();
			}

			// Initial setup
			this.checkVisibility();
			this.updateMeasurements();

			// If no value was provided, emit the default value
			if (this.value === null || isNaN(parseInt(this.value, 10))) {
				this.$emit('input', this.defaultValue);
			}
		});
	},
	beforeDestroy() {
		this.cleanUpListeners();

		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		} else {
			window.removeEventListener('resize', this.handleResize);
		}

		if (this.mutationObserver) {
			this.mutationObserver.disconnect();
		}
	},
	methods: {
		detectParentBackgroundColor() {
			let element = this.$el;
			let bgColor = 'transparent';

			// Check up to 10 parent levels to find a background color
			for (let i = 0; i < 10; i++) {
				const style = window.getComputedStyle(element);
				bgColor = style.backgroundColor;

				// If we found a non-transparent background or reached the body, stop
				if (bgColor !== 'transparent' && bgColor !== 'rgba(0, 0, 0, 0)') {
					break;
				}

				// Move up to parent element
				if (element.parentElement) {
					element = element.parentElement;
				} else {
					break;
				}
			}

			// Default to a dark color if no background was found
			if (bgColor === 'transparent' || bgColor === 'rgba(0, 0, 0, 0)') {
				bgColor = 'var(--one-panel-background-color, #191a1e)';
			}

			this.parentBackgroundColor = bgColor;
		},
		setupVisibilityObserver() {
			// Find parent elements up to a certain level
			let element = this.$el;
			const parents = [element];

			for (let i = 0; i < 5; i++) { // Check up to 5 parent levels
				if (element.parentElement) {
					element = element.parentElement;
					parents.push(element);
				} else {
					break;
				}
			}

			// Create observer for display/visibility changes
			this.mutationObserver = new MutationObserver(mutations => {
				this.checkVisibility();

				// Check if background color might have changed
				const styleChanges = mutations.some(mutation =>
					mutation.attributeName === 'style' ||
					mutation.attributeName === 'class'
				);

				if (styleChanges) {
					this.detectParentBackgroundColor();
				}
			});

			// Observe all parent elements for attribute changes
			const config = { attributes: true, attributeFilter: ['style', 'class'] };
			parents.forEach(parent => {
				this.mutationObserver.observe(parent, config);
			});
		},
		checkVisibility() {
			// Get computed style
			const wasVisible = this.isVisible;
			const element = this.$refs.rulersContainer;

			if (!element) return;

			// Check if the element is visible
			const style = window.getComputedStyle(element);
			this.isVisible = style.display !== 'none' &&
				style.visibility !== 'hidden' &&
				element.offsetParent !== null;

			// If visibility changed, update measurements
			if (wasVisible !== this.isVisible) {
				if (this.isVisible) {
					// Set a small timeout to ensure DOM is updated
					setTimeout(() => {
						this.detectParentBackgroundColor();
						this.updateMeasurements();
					}, 50);
				}
			}
		},
		updateMeasurements() {
			// Get the container width
			const width = this.$refs.rulersContainer.offsetWidth;

			// If width is 0 (element is hidden), keep previous width or use default
			this.containerWidth = width || this.containerWidth || this.defaultWidth;

			// Now update the layout
			this.updateWheelLayout();
		},
		updateWheelLayout() {
			this.updateOffset();
		},
		handleResize(entries) {
			if (entries && entries[0]) {
				// Using ResizeObserver
				const width = entries[0].contentRect.width;
				if (width > 0) {
					this.containerWidth = width;
				}
			} else {
				// Direct measurement
				const width = this.$refs.rulersContainer.offsetWidth;
				if (width > 0) {
					this.containerWidth = width;
				}
			}

			this.updateWheelLayout();
		},
		valueToPosition(value) {
			return ((value - this.min) / this.step) * this.tickWidth;
		},
		positionToValue(position) {
			const valueIndex = Math.round(position / this.tickWidth);
			const value = this.min + (valueIndex * this.step);
			return Math.max(this.min, Math.min(this.max, value));
		},
		updateOffset() {
			// Position the selected value in the center
			const centerX = this.containerWidth / 2;
			const selectedValuePosition = this.valueToPosition(this.currentInternalValue);
			this.offset = centerX - selectedValuePosition;
		},
		handleMouseDown(e) {
			if (this.disabled) return;
			this.onMouseDown(e);
		},
		handleTouchStart(e) {
			if (this.disabled) return;
			this.onTouchStart(e);
		},
		handleWheel(e) {
			if (this.disabled) return;
			this.onWheel(e);
		},
		onMouseDown(e) {
			if (e.button !== 0) return; // Skip if not left mouse button
			e.preventDefault();

			this.dragging = true;
			this.startX = e.clientX;
			this.startOffset = this.offset;

			this._mouseMoveHandler = (evt) => this.onMouseMove(evt);
			this._mouseUpHandler = (evt) => this.onMouseUp(evt);

			document.addEventListener('mousemove', this._mouseMoveHandler);
			document.addEventListener('mouseup', this._mouseUpHandler);
		},
		onMouseMove(e) {
			if (!this.dragging) return;
			e.preventDefault();

			const deltaX = e.clientX - this.startX;
			const newOffset = this.startOffset + deltaX;

			// Calculate new value based on the offset
			const centerPosition = -newOffset + (this.containerWidth / 2);
			const newValue = this.positionToValue(centerPosition);

			if (newValue !== this.currentInternalValue &&
				newValue >= this.min && newValue <= this.max) {
				this.currentInternalValue = newValue;
				this.$emit('input', newValue);
				this.updateOffset(); // Snap to the new value immediately
			}
		},
		onMouseUp(e) {
			if (!this.dragging) return;
			e.preventDefault();

			this.dragging = false;
			this.cleanUpListeners();

			// Make sure we're properly aligned
			this.updateOffset();
		},
		onTouchStart(e) {
			if (e.touches.length !== 1) return; // Skip if not single touch
			e.preventDefault();

			this.dragging = true;
			this.startX = e.touches[0].clientX;
			this.startOffset = this.offset;

			this._touchMoveHandler = (evt) => this.onTouchMove(evt);
			this._touchEndHandler = (evt) => this.onTouchEnd(evt);

			document.addEventListener('touchmove', this._touchMoveHandler, { passive: false });
			document.addEventListener('touchend', this._touchEndHandler);
			document.addEventListener('touchcancel', this._touchEndHandler);
		},
		onTouchMove(e) {
			if (!this.dragging || e.touches.length !== 1) return;
			e.preventDefault();

			const deltaX = e.touches[0].clientX - this.startX;
			const newOffset = this.startOffset + deltaX;

			// Calculate new value based on the offset
			const centerPosition = -newOffset + (this.containerWidth / 2);
			const newValue = this.positionToValue(centerPosition);

			if (newValue !== this.currentInternalValue &&
				newValue >= this.min && newValue <= this.max) {
				this.currentInternalValue = newValue;
				this.$emit('input', newValue);
				this.updateOffset(); // Snap to the new value immediately
			}
		},
		onTouchEnd(e) {
			if (!this.dragging) return;
			e.preventDefault();

			this.dragging = false;
			this.cleanUpListeners();

			// Make sure we're properly aligned
			this.updateOffset();
		},
		onWheel(e) {
			e.preventDefault();

			// Determine direction (positive deltaY means scroll down, negative means scroll up)
			const direction = e.deltaY > 0 ? 1 : -1;
			const newValue = Math.max(this.min, Math.min(this.max, this.currentInternalValue + (direction * this.step)));

			if (newValue !== this.currentInternalValue) {
				this.currentInternalValue = newValue;
				this.$emit('input', newValue);
				this.updateOffset(); // Snap to the new value immediately
			}
		},
		cleanUpListeners() {
			if (this._mouseMoveHandler) {
				document.removeEventListener('mousemove', this._mouseMoveHandler);
				this._mouseMoveHandler = null;
			}

			if (this._mouseUpHandler) {
				document.removeEventListener('mouseup', this._mouseUpHandler);
				this._mouseUpHandler = null;
			}

			if (this._touchMoveHandler) {
				document.removeEventListener('touchmove', this._touchMoveHandler);
				this._touchMoveHandler = null;
			}

			if (this._touchEndHandler) {
				document.removeEventListener('touchend', this._touchEndHandler);
				document.removeEventListener('touchcancel', this._touchEndHandler);
				this._touchEndHandler = null;
			}
		},
	},
};
</script>

<style scoped lang="scss">
$wheel-height: 30px;

.wheel-select {
	--tick-color: var(--color-grey-600);
	--tick-color-active: var(--color-white);
	--tick-color-major: var(--color-grey-400);
	--tick-width: 1px;
	--tick-height: 4px;
	--tick-height-major: 8px;

	position: relative;
	width: 100%;
	height: $wheel-height + 2px;
	display: flex;
	align-items: center;
	justify-content: center;
	user-select: none;
	cursor: grab;

	&:active {
		cursor: grabbing;
	}

	&.is-disabled {
		opacity: 0.5;
		cursor: not-allowed;

		&:active {
			cursor: not-allowed;
		}
	}
}

.wheel-select__ruler-container {
	position: relative;
	width: 100%;
	height: $wheel-height;
	overflow: hidden;
}

.wheel-select__ruler {
	position: absolute;
	bottom: 0;
	left: 0;
	height: $wheel-height;
}

.wheel-select__border {
	position: absolute;
	bottom: 0;
	left: 0;
	height: 1px;
	background-color: var(--color-grey-800);
}

.wheel-select__fade-left,
.wheel-select__fade-right {
	position: absolute;
	top: 0;
	bottom: 0;
	width: 30%;
	z-index: 3; // stylelint-disable-line
	pointer-events: none;
}

.wheel-select__fade-left {
	left: 0;
}

.wheel-select__fade-right {
	right: 0;
}

.wheel-select__tick {
	position: absolute;
	bottom: 0;
	width: var(--tick-width);
	height: var(--tick-height);
	background-color: var(--tick-color);
	opacity: 0.5;

	&--major {
		height: var(--tick-height-major);
		background-color: var(--tick-color-major);
		opacity: 1;
	}

	&--active {
		background-color: var(--tick-color-active);
		opacity: 1;
	}
}

.wheel-select__label {
	position: absolute;
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%);
	color: var(--tick-color-major);
	font-size: 10px; // stylelint-disable-line
	font-weight: 600; // stylelint-disable-line
	pointer-events: none;
	white-space: nowrap;
	margin-bottom: 2px;
}

.wheel-select__center-indicator {
	position: absolute;
	left: 50%;
	bottom: 0;
	width: 1px;
	height: 30px;
	background-color: var(--tick-color-active);
	transform: translateX(-50%);
	z-index: 2;
}

/* This is for pixel-perfect positioning */
@media screen and (-webkit-min-device-pixel-ratio: 0) { // stylelint-disable-line
	.wheel-select__center-indicator {
		left: calc(50% + 0.5px);
	}
}
</style>
